import { checkPaymentStatus } from "@/pages/QuestionsPage/ui/QuestionSearch/questionsPaymentApi";
import {
    PERSONAL_DATA_WORKER_URL,
    QUOTA_PATH,
    saveQuestionQuota,
    walletToken,
    type AnswerPackage,
} from "@/pages/QuestionsPage/ui/QuestionSearch/questionsQuotaApi";
import { trackTokenPackPurchase } from "@/pages/QuestionsPage/ui/QuestionSearch/tokenPackAnalytics";
import { isPlansModeNow, loadPlanWallet } from "@/features/supPlan/model/planStore";

/**
 * Завершить покупку токенов на той странице, куда вернулись со Stripe.
 *
 * Обычно завершать нечего: сервер зачислил пакет ещё до переадресации
 * (site_server payments.stripe_success). Здесь — сверка и запасной путь,
 * как раньше делала страница «Умного поиска»: не успел сервер — пакет пишет
 * сайт.
 *
 * ОДИН ПАКЕТ НА ЗАКАЗ. Пакет заказа лежит в quota.json под id
 * `stripe-<session_id>` — так пишут и вебхук, и возврат на сервере
 * (payments.token_pack_id), и этот модуль. Кто пришёл вторым — находит пакет и
 * ничего не добавляет.
 */

/** Как в sessionStorage помнят выбранный пакет до ухода на Stripe. */
export const PENDING_PACK_KEY = "pending_questions_package";

/** Id пакета этого заказа в quota.json — как payments.token_pack_id на сервере. */
export const tokenPackageId = (sessionId: string): string => `stripe-${sessionId}`;

export interface PendingPack {
    count: number;
    days: number;
    /** Пакет (tok-35…) — для цены покупки в GA4; у старых записей его нет. */
    sku?: string;
}

const takePendingPack = (): PendingPack | null => {
    let raw: string | null = null;
    try {
        raw = sessionStorage.getItem(PENDING_PACK_KEY);
        sessionStorage.removeItem(PENDING_PACK_KEY);
    } catch {
        return null;
    }
    if (!raw) return null;
    try {
        const p = JSON.parse(raw) as Partial<PendingPack>;
        const count = Number(p.count);
        const days = Number(p.days);
        if (!(count > 0 && days > 0)) return null;
        return typeof p.sku === "string" && p.sku ? { count, days, sku: p.sku } : { count, days };
    } catch {
        return null;
    }
};

interface Wallet {
    packages: AnswerPackage[];
    viewed?: unknown;
    [key: string]: unknown;
}

/**
 * Кошелёк как он есть. Не прочитался — ошибка, а не «кошелёк по умолчанию»:
 * записать поверх него пакет значило бы стереть настоящий баланс.
 */
const readWallet = async (token: string): Promise<Wallet> => {
    const res = await fetch(`${PERSONAL_DATA_WORKER_URL}/data/${QUOTA_PATH}`, {
        method: "GET",
        headers: { Authorization: `Bearer ${token}` },
    });
    if (res.status === 404) return { version: 2, packages: [], viewed: [] };
    if (!res.ok) throw new Error(`quota ${res.status}`);
    const data = await res.json();
    if (!data || data.version !== 2 || !Array.isArray(data.packages)) {
        throw new Error("quota: not v2");
    }
    return data as Wallet;
};

export type TokenPurchaseResult =
    /** Пакет записан сейчас (сервер не успел). */
    | "credited"
    /** Пакет уже на кошельке — так обычно и бывает: его записал сервер. */
    | "already"
    /** Оплата не подтверждена. */
    | "unpaid"
    /** Не узнать: нет сессии, кошелёк не прочитался, неизвестен пакет. */
    | "unknown";

/**
 * Повторять можно: пакет заказа один (tokenPackageId). `fallbackPack` —
 * старый возврат на «Умный поиск»: там без запомненного пакета зачисляли
 * 30 на 30 дней.
 */
export const completeTokenPurchase = async (
    sessionId: string,
    opts: { token?: string; fallbackPack?: PendingPack } = {},
): Promise<TokenPurchaseResult> => {
    const status = await checkPaymentStatus(sessionId);
    if (status !== "paid") return "unpaid";
    const taken = takePendingPack();
    // GA4 purchase — as soon as Stripe says paid, before the wallet: a buyer
    // whose session expired is still a sale. Only a pack this tab chose (the
    // 30/30 fallback is a guess); the server reports every order anyway.
    if (taken) trackTokenPackPurchase(sessionId, taken.sku, taken.count);
    // Режим пакетов (features/supPlan): старый пакет токенов из старой вкладки
    // сервер уже превратил в вопросы и поиск — браузер ничего не дописывает.
    // Режим сначала узнать: на новом устройстве вид ещё не читали, и «не знаю» —
    // не «токены». Не прочитался вид у пакетного / сервер не знает режима — тоже
    // ничего не пишем: зачислит сервер (вебхук повторится).
    try {
        const plan = await loadPlanWallet();
        if (plan?.mode === "plans") return "already";
    } catch {
        return "unknown";
    }
    if (isPlansModeNow()) return "already";
    const pending = taken ?? opts.fallbackPack ?? null;
    const token = opts.token ?? walletToken();
    if (!token) return "unknown";
    let wallet: Wallet;
    try {
        wallet = await readWallet(token);
    } catch {
        return "unknown";
    }
    const id = tokenPackageId(sessionId);
    if (wallet.packages.some((p) => p && p.id === id)) return "already";
    if (!pending) return "unknown";
    const now = new Date();
    const expires = new Date(now);
    expires.setDate(expires.getDate() + pending.days);
    // Как createPurchasePackage, но с id заказа, а не случайным.
    const pack: AnswerPackage = {
        id,
        count: pending.count,
        initialCount: pending.count,
        expiresAt: expires.toISOString(),
        source: "purchase",
        createdAt: now.toISOString(),
    };
    try {
        await saveQuestionQuota(token, {
            ...wallet,
            version: 2,
            packages: [...wallet.packages, pack],
            viewed: Array.isArray(wallet.viewed) ? (wallet.viewed as string[]) : [],
        });
    } catch {
        return "unknown";
    }
    return "credited";
};
